import { useState, useEffect, useRef } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

// Custom dropdown with the dashboard's design language: dark inset
// surface, subtle white border, brand-orange focus ring, and a small
// brand check-mark on the selected option (instead of a heavy filled
// row). Closes on outside click and falls back to a disabled-look
// state when no options are available.

export default function CustomDropdown({
    label,
    value,
    onChange,
    options = [],
    disabled = false,
    placeholder = 'Select...',
}) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Drop options that are null/undefined or missing a `value`. Defensive
    // because callers sometimes spread arrays from RTDB which may have
    // sparse entries.
    const validOptions = options.filter((opt) => opt != null && opt.value !== undefined);

    const isTrulyEmpty = validOptions.length === 0;
    const isAutoDisabled = validOptions.length <= 1;
    const isDisabled = disabled || isTrulyEmpty || isAutoDisabled;

    const selectedOption = validOptions.find((opt) => opt.value === value);
    const displayLabel = selectedOption?.label || (isTrulyEmpty ? 'No options available' : placeholder);
    const hasSelection = Boolean(selectedOption);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative w-full" ref={dropdownRef}>
            {label && (
                <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">
                    {label}
                </label>
            )}

            <button
                type="button"
                disabled={isDisabled}
                onClick={() => setOpen((prev) => !prev)}
                className={`w-full bg-surface-inset border rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between gap-2 transition-colors focus:outline-none ${
                    open
                        ? 'border-brand/40 ring-1 ring-brand/40'
                        : 'border-white/[0.08] hover:border-white/[0.15]'
                } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${
                    hasSelection ? 'text-white' : 'text-gray-500'
                }`}
            >
                <span className="truncate" title={displayLabel}>{displayLabel}</span>
                <FiChevronDown
                    className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && !isDisabled && (
                <ul
                    role="listbox"
                    className="absolute z-20 w-full mt-1 bg-surface-card border border-white/[0.08] rounded-lg shadow-xl max-h-60 overflow-auto py-1"
                >
                    {validOptions.map((opt) => {
                        const isSelected = value === opt.value;
                        return (
                            <li
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => {
                                    if (!opt.disabled) {
                                        onChange(opt.value);
                                        setOpen(false);
                                    }
                                }}
                                className={`px-3 py-1.5 mx-1 rounded-md flex items-center justify-between gap-2 text-sm cursor-pointer transition-colors ${
                                    opt.disabled
                                        ? 'text-gray-500 cursor-not-allowed'
                                        : isSelected
                                            ? 'text-white bg-white/[0.04]'
                                            : 'text-gray-300 hover:bg-white/[0.05] hover:text-white'
                                }`}
                            >
                                <span className="truncate" title={opt.label}>{opt.label}</span>
                                {isSelected && (
                                    <FiCheck className="shrink-0 text-brand-400" />
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
